import { useState, useEffect, useCallback } from 'react';
import type { StudyCase } from '@segmently/schema-models/entities';
import { StudyCaseService } from '@modules/firebase-core/services/StudyCaseService';

export function useStudyCases(projectId: string) {
  const [studyCases, setStudyCases] = useState<StudyCase[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!projectId) return;
    setLoading(true);
    setError('');

    const service = new StudyCaseService(projectId);
    const unsub = service.subscribe(
      (data) => { setStudyCases(data); setLoading(false); },
      (err) => { console.error('Error listening study cases:', err); setError('Failed to load study cases.'); setLoading(false); },
    );
    return () => unsub();
  }, [projectId]);

  const createStudyCase = useCallback(async (data: Partial<StudyCase>) => {
    const service = new StudyCaseService(projectId);
    return service.create(data);
  }, [projectId]);

  const updateStudyCase = useCallback(async (id: string, data: Partial<StudyCase>) => {
    const service = new StudyCaseService(projectId);
    return service.update(id, data);
  }, [projectId]);

  const deleteStudyCase = useCallback(async (id: string) => {
    const service = new StudyCaseService(projectId);
    return service.delete(id);
  }, [projectId]);

  const getOutputsService = useCallback((studyCaseId: string) => {
    const service = new StudyCaseService(projectId);
    return service.getOutputsService(studyCaseId);
  }, [projectId]);

  return { studyCases, loading, error, setError, createStudyCase, updateStudyCase, deleteStudyCase, getOutputsService };
}
